import React, { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
	CarIcon,
	SailboatIcon,
	VanIcon,
	TractorIcon,
	TruckTrailerIcon,
	HouseLineIcon,
	HandCoinsIcon,
	ArrowClockwiseIcon,
} from "@phosphor-icons/react";
import {
	emailTypos,
	LOAN_PURPOSE_BUY_A_HOME,
	LOAN_PURPOSE_PERSONAL,
	LOAN_PURPOSE_REFINANCE_MORTGAGE,
	LOAN_TYPE_BOAT,
	LOAN_TYPE_CAR,
	LOAN_TYPE_CARAVAN,
	LOAN_TYPE_EQUIPMENT,
	LOAN_TYPE_MORTGAGE,
	LOAN_TYPE_TRUCK,
	loanFieldsMap,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { ToggleSwitch } from "@/components/ui";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import { secureInput } from "@/lib/security";

interface Step1Props {
	loanType?: string;
}

export const PersonalDetails: React.FC<Step1Props> = ({ loanType }) => {
	// Get values and setters from Zustand store
	const { formValues, setFormValue, setFormError, formErrors } = useFormDataStore();
	const {
		selectedIndex,
		loanAmountMin,
		setFormErrors,
		enabledFields,
		setEnabledFields,
		enabledLoans,
		setValidateFormDetails,
		formContainer,
		toggleTC,
		setToggleTC,
		hasAttemptedSubmit,
	} = useFormStateStore();

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		firstName: false,
		lastName: false,
		email: false,
		loanType: false,
		loanAmount: false,
		tc: false,
		loanPurpose: false,
	});

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	// Toggle Switch for T&C
	const [enabledTC, setEnabledTC] = useState(toggleTC);

	useEffect(() => {
		setToggleTC(enabledTC);
		if (errors.tc) {
			setErrors((prev) => ({ ...prev, tc: !enabledTC }));
		}
	}, [enabledTC]);

	const { firstName, lastName, email, loanAmount, loanType: zLoanType, loanPurpose } = formValues;

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = 430;
		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	// Email Typo Validation Helper
	const getEmailValidationError = (emailValue: string): string => {
		const trimmed = emailValue.trim();
		if (trimmed === "") return "Email is required";

		const sanitizedValue = secureInput.email(trimmed);
		if (!sanitizedValue.isValid) return "Email is not correctly formatted";

		const fullValue = trimmed.toLowerCase();
		const parts = fullValue.split("@");

		if (parts.length === 2) {
			const [username, domain] = parts;
			const typoRegex = /\.(con\.au|cm\.au|con|cm|co)$/i;
			const endTypoRegex = /\.com[a-z]$/;

			if (emailTypos[domain]) {
				return `Did you mean ${username}@${emailTypos[domain]}?`;
			} else if (typoRegex.test(domain)) {
				const fixedDomain = domain.replace(typoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			} else if (endTypoRegex.test(domain)) {
				const fixedDomain = domain.replace(endTypoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			}
		}
		return "";
	};

	// Handle input changes with validation
	const handleFirstNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("firstName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, firstName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleLastNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("lastName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, lastName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const sanitizedValue = secureInput.email(value);
		setFormValue("email", sanitizedValue.sanitized);

		const emailErrorMsg = getEmailValidationError(sanitizedValue.sanitized);
		setErrors((prev) => ({ ...prev, email: emailErrorMsg !== "" }));
		setFormError("email", emailErrorMsg);
	};

	const handleBlur = (field: "firstName" | "lastName" | "email", value: string) => {
		if (!value) return;

		if (field === "email") {
			setFormValue("email", value.toLowerCase());
		} else {
			// Capitalise first character, and convert ALL-CAPS strings to Capitalised
            const forcedCasing =
                value === value.toUpperCase()
                    ? value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
                    : value.charAt(0).toUpperCase() + value.slice(1);

            setFormValue(field, forcedCasing);
		}
	};

	// handle loan purpose change
	const handleLoanPurposeChange = useCallback(
		(value: string) => {
			setFormValue("loanPurpose", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				loanPurpose: !value,
			}));

			if (value === LOAN_PURPOSE_BUY_A_HOME) {
				setFormValue("interestRate", "");
				setFormValue("fixedRate", "");
				setFormValue("lenderName", "");
				setFormValue("propertyValue", "");
				setFormValue("currentMortgage", "");
				setFormValue("refinancePurpose", "");
			} else if (value === LOAN_PURPOSE_REFINANCE_MORTGAGE) {
				setFormValue("purchasePrice", "");
				setFormValue("depositAmount", "");
				setFormValue("firstHomeBuyer", "");
				setFormValue("purchaseStage", "");
				setFormValue("assetType", "");
			}
		},
		[setFormValue],
	);

	const handleLoanAmountChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("loanAmount", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				loanAmount: formattedValue < loanAmountMin,
			}));
		},
		[setFormValue, loanAmountMin],
	);

	const handleLoanTypeChange = useCallback(
		(value: string) => {
			setFormValue("loanType", value);
			setErrors((prev) => ({ ...prev, loanType: !value }));
			if (value) {
				setEnabledFields(value);
			}

			if (
				loanFieldsMap[value]?.loanPurpose[0].value === LOAN_PURPOSE_PERSONAL ||
				(loanFieldsMap[value]?.loanPurpose.length == 2 &&
					loanFieldsMap[value]?.loanPurpose[1].value === LOAN_PURPOSE_PERSONAL)
			) {
				setFormValue("abnStatus", "");
				setFormValue("gstStatus", "");
			}
		},
		[setFormValue, setEnabledFields],
	);

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const emailErrorMsg = getEmailValidationError(email);

		const newErrors = {
			firstName: !secureInput.name(firstName).isValid,
			lastName: !secureInput.name(lastName).isValid,
			email: emailErrorMsg !== "",
			loanAmount:
				loanType !== LOAN_TYPE_MORTGAGE &&
				(!loanAmount || parseFloat(loanAmount.replace(/,/g, "")) < loanAmountMin),
			loanType: !loanType,
			tc: !enabledTC,
			loanPurpose: formValues.loanType === LOAN_TYPE_MORTGAGE ? !loanPurpose : false,
		};

		setErrors(newErrors);
		setFormError("email", emailErrorMsg);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [firstName, lastName, email, loanAmount, zLoanType, loanType, enabledTC, loanPurpose]);

	return (
		<div className="yF-space-y-3">
			<div>
				<label
					htmlFor="firstName"
					className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900"
				>
					Contact Details
				</label>
				<div
					className={clsx(
						isNavDivSmall
							? "yF-flex-col yF-gap-x-2 yF-gap-y-1"
							: "yF-flex-row yF-gap-x-2",
						"yF-flex",
					)}
				>
					<div className="yF-flex-1">
						<div className="yF-relative yF-mt-1">
							<input
								value={firstName}
								onChange={handleFirstNameChange}
								onBlur={(e) => handleBlur("firstName", e.target.value)}
								onFocus={() => handleFocus("firstName")}
								aria-label="First Name"
								type="text"
								id="firstName"
								placeholder="First Name"
								maxLength={30}
								autoComplete="given-name"
								className={clsx(
									"yF-capitalize",
									hasAttemptedSubmit && errors.firstName
										? "yF-ring-red-500 focus:yF-ring-red-500"
										: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
									"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-text-gray-900 yF-ring-1 yF-outline-1 yF-outline-gray-900 yF-ring-inset focus:yF-ring-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] yF-pl-3 placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
								)}
							/>
						</div>
						{hasAttemptedSubmit && errors.firstName && (
							<span className="yF-text-xs yF-text-red-500">First Name Required</span>
						)}
					</div>
					<div className="yF-flex-1">
						<div className="yF-relative yF-mt-1">
							<input
								value={lastName}
								onChange={handleLastNameChange}
								onBlur={(e) => handleBlur("lastName", e.target.value)}
								onFocus={() => handleFocus("lastName")}
								aria-label="Last Name"
								type="text"
								id="lastName"
								placeholder="Last Name"
								maxLength={30}
								autoComplete="family-name"
								className={clsx(
									"yF-capitalize",
									hasAttemptedSubmit && errors.lastName
										? "yF-ring-red-500 focus:yF-ring-red-500"
										: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
									"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-text-gray-900 yF-ring-1 yF-outline-1 yF-outline-gray-900 yF-ring-inset focus:yF-ring-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] yF-pl-3 placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
								)}
							/>
						</div>
						{hasAttemptedSubmit && errors.lastName && (
							<span className="yF-text-xs yF-text-red-500">Last Name Required</span>
						)}
					</div>
				</div>

				<div className="yF-relative yF-mt-2">
					<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
						<EnvelopeIcon
							className="yF-h-5 yF-w-5 yF-text-gray-400"
							aria-hidden="true"
						/>
					</div>
					<input
						value={email}
						onFocus={() => {
							handleFocus("email");
							setFormError("email", "");
						}}
						onChange={handleEmailChange}
						onBlur={(e) => handleBlur("email", e.target.value)}
						aria-label="Email Address"
						type="email"
						id="email"
						placeholder="Email Address"
						maxLength={50}
						autoComplete="email"
						className={clsx(
							(hasAttemptedSubmit && errors.email) || formErrors.email
								? "yF-ring-red-500 focus:yF-ring-red-500"
								: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
							"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-pl-10 yF-text-gray-900 yF-ring-1 yF-outline-1 yF-outline-gray-900 yF-ring-inset focus:yF-ring-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6 yF-bg-none",
						)}
					/>
				</div>
				{formErrors.email && (
					<span className="yF-text-xs yF-text-red-500">{formErrors.email}</span>
				)}
			</div>

			{/* Loan Types */}
			{enabledLoans.length > 1 && (
				<RadioGroupField
					id="loanType"
					label="Choose a Loan Type"
					value={loanType || ""}
					options={enabledLoans.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_TYPE_CAR ? (
								<CarIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_BOAT ? (
								<SailboatIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_TRUCK ? (
								<TruckTrailerIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_EQUIPMENT ? (
								<TractorIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_CARAVAN ? (
								<VanIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_MORTGAGE ? (
								<HouseLineIcon size={24} weight="duotone" />
							) : undefined,
					}))}
					onChange={(value) => {
						handleLoanTypeChange(value);
					}}
					required={true}
					error={hasAttemptedSubmit && errors.loanType ? "This field is required" : ""}
					disabled={false}
					className="yF-mt-4"
					gridCols={3}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			{enabledFields.loanPurpose?.length > 0 && loanType === LOAN_TYPE_MORTGAGE && (
				<RadioGroupField
					id="loanPurpose"
					label="Mortgage Loan Purpose"
					value={loanPurpose || ""}
					options={enabledFields.loanPurpose.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_PURPOSE_BUY_A_HOME ? (
								<HandCoinsIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_REFINANCE_MORTGAGE ? (
								<ArrowClockwiseIcon size={24} weight="duotone" />
							) : null,
					}))}
					onChange={(value) => {
						handleLoanPurposeChange(value);
					}}
					required={true}
					error={errors.loanPurpose ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={2}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			{loanType !== LOAN_TYPE_MORTGAGE && (
				<div
					className={clsx(
						isNavDivSmall
							? "yF-flex-col yF-gap-x-2  yF-space-y-2"
							: "yF-flex-row yF-gap-x-2",
						"yF-flex",
					)}
				>
					{loanType !== LOAN_TYPE_MORTGAGE && (
						<div className="yF-flex-1">
							<label
								htmlFor="loan-amount"
								className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900"
							>
								Loan Amount
							</label>
							<div className="yF-relative yF-mt-1">
								<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
									<span className="yF-text-gray-500 sm:yF-text-md">$</span>
								</div>
								<input
									value={loanAmount}
									onChange={handleLoanAmountChange}
									onFocus={() => handleFocus("loanAmount")}
									aria-label="Loan Amount"
									placeholder={
										loanAmountMin >= 10000
											? `Loan Amount (Min 10K)`
											: loanAmountMin == 5000
												? `Loan Amount (Min 5K)`
												: loanAmountMin == 2000
													? `Loan Amount (Min 2K)`
													: ``
									}
									maxLength={10}
									className={clsx(
										hasAttemptedSubmit && errors.loanAmount
											? "yF-ring-red-500 focus:yF-ring-red-500"
											: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
										"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-pl-7 yF-pr-12 yF-text-gray-900 yF-ring-1 yF-ring-gray-300 focus:yF-ring-1 yF-outline-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
									)}
									type="text"
									id="loan-amount"
									aria-describedby="loan-amount"
									inputMode="numeric"
								/>
								<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-right-0 yF-flex yF-items-center yF-pr-3">
									<span className="yF-text-gray-500 sm:yF-text-md">AUD</span>
								</div>
							</div>
							{hasAttemptedSubmit && errors.loanAmount && (
								<span className="yF-text-xs yF-text-red-500">
									Minimum loan amount is ${loanAmountMin.toLocaleString()}
								</span>
							)}
						</div>
					)}
				</div>
			)}

			<div id="tc-toggle" className={clsx("!yF-mt-4")} tabIndex={-1}>
				<ToggleSwitch label="" enabled={enabledTC} setEnabled={setEnabledTC} />
				{hasAttemptedSubmit && errors.tc && (
					<span className="yF-block yF-mt-1 yF-text-xs yF-text-red-500">
						Please agree to the terms and conditions to continue
					</span>
				)}
			</div>
		</div>
	);
};
